import { gql, useMutation } from '@apollo/client';
import { Modal, useToast } from '@chakra-ui/react';
import React from 'react';

import { FieldValidationError } from '@/client/legacy-shared-modules/csv/csv-field-mapping';
import {
  ValidationFn,
  getValidateCsvFileHeaders,
  normalizeHeader,
} from '@/client/legacy-shared-modules/csv/helpers';
import { useUploadFileToS3 } from '@/hooks/useUploadFileToS3';
import { logError } from '@/imports/logging/ClientLogger';
import { SuccessModal } from '@/imports/ui/admin/components/SuccessModal';
import { CsvImportModalFrameEnhanced } from '@/imports/ui/components/csv/CsvImportModalFrameEnhanced';
import { useDownloadUserGroupLinkCsvErrors } from '@/imports/ui/pages/morgoth/useDownloadUserGroupLinkCsvErrors';

import {
  GroupsUserCsvImportModal_ExecuteSpecificGroupMemberCsvUpload,
  GroupsUserCsvImportModal_ExecuteSpecificGroupMemberCsvUploadVariables,
} from './graphql-types/GroupsUserCsvImportModal_ExecuteSpecificGroupMemberCsvUpload';

import {
  ESpecificGroupMemberCsvUploadFieldNames,
  SPECIFIC_GROUP_MEMBER_CSV_UPLOAD_TEMPLATE,
} from './groupUserImportCsvTemplate';
import { usePrepareSpecificGroupMemberCsvUpload } from './hooks/usePrepareSpecificGroupMemberCsvUpload';

const EXECUTE_SPECIFIC_GROUP_MEMBER_CSV_UPLOAD = gql`
  mutation GroupsUserCsvImportModal_ExecuteSpecificGroupMemberCsvUpload(
    $input: ExecuteSpecificGroupMemberCsvUploadInput!
  ) {
    executeSpecificGroupMemberCsvUpload(input: $input) {
      csvUploadId
    }
  }
`;

type GroupUserCsvImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
};

export const GroupUserCsvImportModal: React.FC<GroupUserCsvImportModalProps> = (
  props
) => {
  const [
    executeSpecificGroupMemberCsvUpload,
    executeSpecificGroupMemberCsvUploadResults,
  ] = useMutation<
    GroupsUserCsvImportModal_ExecuteSpecificGroupMemberCsvUpload,
    GroupsUserCsvImportModal_ExecuteSpecificGroupMemberCsvUploadVariables
  >(EXECUTE_SPECIFIC_GROUP_MEMBER_CSV_UPLOAD);

  const [csvFile, setCsvFile] = React.useState<File | null>(null);
  const [validationErrors, setValidationErrors] =
    React.useState<FieldValidationError | null>(null);
  const [
    shouldRemoveAllOtherGroupMembers,
    setShouldRemoveAllOtherGroupMembers,
  ] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] =
    React.useState<boolean>(false);
  const [uploadedCsv, setUploadedCsv] = React.useState<boolean>(false);
  const toast = useToast();
  useDownloadUserGroupLinkCsvErrors({
    validRoutes: [`community/groups`],
  });

  const {
    prepSpecificGroupMemberCsvUpload,
    prepSpecificGroupMemberCsvUploadData,
    prepSpecificGroupMemberCsvUploadLoading,
    clearSpecificGroupMemberCsvUploadResult,
  } = usePrepareSpecificGroupMemberCsvUpload();

  const [uploadFileToS3, { isUploading, error: uploadError, isCanceled }] =
    useUploadFileToS3();

  const prepSpecificGroupMemberCsvUploadResultData =
    prepSpecificGroupMemberCsvUploadData?.prepSpecificGroupMemberCsvUpload;

  const handleUploadFileToS3 = React.useCallback(
    async (file: File): Promise<void> => {
      try {
        if (
          prepSpecificGroupMemberCsvUploadResultData?.metadataHeaders &&
          prepSpecificGroupMemberCsvUploadResultData?.presignedS3Url
        ) {
          const newMetadataHeaders =
            prepSpecificGroupMemberCsvUploadResultData.metadataHeaders.reduce(
              (obj: Record<string, string>, { name, value }) => (
                (obj[name] = value), obj
              ),
              {}
            );

          await uploadFileToS3({
            file,
            presignedS3Url:
              prepSpecificGroupMemberCsvUploadResultData.presignedS3Url,
            metadataHeaders: newMetadataHeaders,
          });
        } else {
          // eslint-disable-next-line quotes
          logError(new Error("Can't upload csv file with failed prep"));
        }
      } catch (err) {
        toast({
          title: 'Failed to upload csv. Please try again.',
          status: 'error',
        });
        logError(err);
      }
    },
    [toast, uploadFileToS3, prepSpecificGroupMemberCsvUploadResultData]
  );

  React.useEffect(() => {
    if (
      prepSpecificGroupMemberCsvUploadResultData &&
      !uploadedCsv &&
      !!csvFile
    ) {
      setUploadedCsv(true);
      handleUploadFileToS3(csvFile).catch((err) => logError(err));
    }
  }, [
    prepSpecificGroupMemberCsvUploadResultData,
    setUploadedCsv,
    handleUploadFileToS3,
    csvFile,
    uploadedCsv,
  ]);

  const handleExecuteAdminUserGroupLinkCsvUpload = async (
    csvUploadId: string
  ): Promise<void> => {
    try {
      await executeSpecificGroupMemberCsvUpload({
        variables: {
          input: {
            csvUploadId,
            groupId: props.groupId,
            shouldRemoveAllOtherUsers: shouldRemoveAllOtherGroupMembers,
          },
        },
      });
      setShowSuccessModal(true);
    } catch (err) {
      toast({
        title: 'Failed to execute user csv upload. Please try again.',
        status: 'error',
      });
      logError(err);
    }
  };

  const getValidateFileFn = React.useCallback((): ((
    file: File
  ) => Promise<void>) => {
    const validateHeaders: ValidationFn = (headers) => {
      if (
        !headers.includes(
          normalizeHeader(ESpecificGroupMemberCsvUploadFieldNames.USER_EMAIL)
        )
      ) {
        return {
          errors: [
            `Missing required field: ${ESpecificGroupMemberCsvUploadFieldNames.USER_EMAIL}`,
          ],
        };
      }

      return null;
    };
    return async (file) => {
      await getValidateCsvFileHeaders({
        validateHeaders,
        onSuccess: (successFile) => {
          setValidationErrors(null);
          setCsvFile(successFile);
        },
        onErrors: (errors) => {
          setCsvFile(null);
          setValidationErrors(errors);
        },
      })(file);
    };
  }, []);

  React.useEffect(() => {
    if (!csvFile) {
      return;
    }
    void getValidateFileFn()(csvFile);
  }, [csvFile, getValidateFileFn]);

  const onClose = (): void => {
    setValidationErrors(null);
    setCsvFile(null);
    setUploadedCsv(false);
    clearSpecificGroupMemberCsvUploadResult();
    props.onClose();
  };

  const handleRemoveFile = (e: React.SyntheticEvent): void => {
    e.stopPropagation();
    setValidationErrors(null);
    setCsvFile(null);
    setUploadedCsv(false);
    clearSpecificGroupMemberCsvUploadResult();
  };

  const submitFileUpload = async (csvUploadId: string): Promise<void> => {
    await handleExecuteAdminUserGroupLinkCsvUpload(csvUploadId);
    onClose();
  };

  const onFileChange = async (file: File | null): Promise<void> => {
    clearSpecificGroupMemberCsvUploadResult();
    await prepSpecificGroupMemberCsvUpload();
    if (!!file) {
      const validateFunc = getValidateFileFn();
      await validateFunc(file);
    }
  };

  const hasLoadingSideEffect =
    isUploading ||
    prepSpecificGroupMemberCsvUploadLoading ||
    executeSpecificGroupMemberCsvUploadResults.loading;

  const isDisabled =
    isCanceled ||
    !!uploadError ||
    hasLoadingSideEffect ||
    !!(validationErrors && validationErrors.errors) ||
    !csvFile;

  const specificGroupMemberCsvImport = (
    <CsvImportModalFrameEnhanced
      pluralEntityName="Group Members"
      footerProps={{
        primaryAction: {
          onClick: async () => {
            if (prepSpecificGroupMemberCsvUploadResultData?.csvUploadId) {
              await submitFileUpload(
                prepSpecificGroupMemberCsvUploadResultData.csvUploadId
              );
            } else {
              logError(new Error('missing csvUploadId'));
            }
          },
          content: 'Upload',
          loading: hasLoadingSideEffect,
          disabled: isDisabled,
        },
      }}
      template={SPECIFIC_GROUP_MEMBER_CSV_UPLOAD_TEMPLATE}
      file={csvFile}
      onFileChange={onFileChange}
      onRemoveFile={handleRemoveFile}
      validationErrors={validationErrors}
      hasLoadingSideEffect={hasLoadingSideEffect}
      removeAllOtherGroupMembersToggleProps={{
        shouldRemoveAllOtherGroupMembers,
        setShouldRemoveAllOtherGroupMembers,
        label: 'Remove group members who are not included in the CSV file',
      }}
    />
  );

  if (showSuccessModal) {
    return (
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    );
  }

  return (
    <Modal isOpen={props.isOpen} onClose={onClose} size="lg">
      {specificGroupMemberCsvImport}
    </Modal>
  );
};
